import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { formatDate } from '../lib/utils';
import axios from 'axios';
import { Plus, Search, Edit2, Trash2, Users, Key, UserCheck, UserX, Shield, ShieldCheck, ShieldAlert, Truck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roles = [
  { value: 'admin', label_en: 'Admin', label_ar: 'مدير', icon: ShieldCheck, color: 'text-red-600 bg-red-100' },
  { value: 'accountant', label_en: 'Accountant', label_ar: 'محاسب', icon: Shield, color: 'text-blue-600 bg-blue-100' },
  { value: 'sales', label_en: 'Sales', label_ar: 'مبيعات', icon: ShieldAlert, color: 'text-green-600 bg-green-100' },
  { value: 'driver', label_en: 'Driver', label_ar: 'سائق', icon: Truck, color: 'text-amber-600 bg-amber-100' },
];

export default function UsersPage() {
  const { t, language } = useLanguage();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'sales',
  });
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.id}`, {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          is_active: editingUser.is_active,
        });
      } else {
        await axios.post(`${API}/users`, formData);
      }
      fetchUsers();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving user:', error);
      alert(error.response?.data?.detail || 'Error saving user');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword,
      });
      setPasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
      alert(language === 'ar' ? 'تم تغيير كلمة المرور بنجاح' : 'Password reset successfully');
    } catch (error) {
      console.error('Error resetting password:', error);
      alert(error.response?.data?.detail || 'Error resetting password');
    }
  };

  const handleToggleStatus = async (user) => {
    if (user.id === currentUser.id) {
      alert(language === 'ar' ? 'لا يمكنك تعطيل حسابك' : 'Cannot deactivate your own account');
      return;
    }
    try {
      await axios.put(`${API}/users/${user.id}/toggle-status`);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      alert(error.response?.data?.detail || 'Error toggling status');
    }
  };

  const handleDelete = async (user) => {
    if (user.id === currentUser.id) {
      alert(language === 'ar' ? 'لا يمكنك حذف حسابك' : 'Cannot delete your own account');
      return;
    }
    const confirmMsg = language === 'ar' 
      ? `هل أنت متأكد من حذف المستخدم "${user.name}"؟`
      : `Are you sure you want to delete user "${user.name}"?`;
    
    if (window.confirm(confirmMsg)) {
      try {
        await axios.delete(`${API}/users/${user.id}`);
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert(error.response?.data?.detail || 'Error deleting user');
      }
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'sales',
    });
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const roleData = roles.find((r) => r.value === role);
    if (!roleData) return null;
    const Icon = roleData.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${roleData.color}`}>
        <Icon size={12} />
        {language === 'ar' ? roleData.label_ar : roleData.label_en}
      </span>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl" data-testid="users-title">
            {language === 'ar' ? 'إدارة المستخدمين' : 'User Management'}
          </h1>
          <p className="text-muted-foreground">
            {language === 'ar' ? 'إدارة حسابات المستخدمين والصلاحيات' : 'Manage user accounts and permissions'}
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-user-btn">
          <Plus size={18} className="me-2" />
          {language === 'ar' ? 'إضافة مستخدم' : 'Add User'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {roles.map((role) => {
          const count = users.filter((u) => u.role === role.value).length;
          const Icon = role.icon;
          return (
            <Card key={role.value} className="border-border/40">
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${role.color}`}>
                  <Icon size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-sm text-muted-foreground">
                    {language === 'ar' ? role.label_ar : role.label_en}
                  </p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Search */}
      <Card className="border-border/40">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              placeholder={t('search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ps-10"
              data-testid="users-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="border-border/40">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users size={48} className="mx-auto mb-2 opacity-50" />
              <p>{t('noData')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('name')}</TableHead>
                    <TableHead>{t('email')}</TableHead>
                    <TableHead>{language === 'ar' ? 'الصلاحية' : 'Role'}</TableHead>
                    <TableHead>{language === 'ar' ? 'الحالة' : 'Status'}</TableHead>
                    <TableHead>{language === 'ar' ? 'تاريخ الإنشاء' : 'Created'}</TableHead>
                    <TableHead className="text-end">{language === 'ar' ? 'الإجراءات' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {user.name}
                          {user.id === currentUser.id && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                              {language === 'ar' ? 'أنت' : 'You'}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell dir="ltr">{user.email}</TableCell>
                      <TableCell>{getRoleBadge(user.role)}</TableCell>
                      <TableCell>
                        {user.is_active !== false ? (
                          <span className="inline-flex items-center gap-1 text-green-600">
                            <UserCheck size={16} />
                            {language === 'ar' ? 'نشط' : 'Active'}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-600">
                            <UserX size={16} />
                            {language === 'ar' ? 'معطل' : 'Inactive'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(user.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openPasswordDialog(user)}
                            title={language === 'ar' ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}
                            data-testid={`reset-password-${user.id}`}
                          >
                            <Key size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleStatus(user)}
                            disabled={user.id === currentUser.id}
                            title={user.is_active !== false 
                              ? (language === 'ar' ? 'تعطيل' : 'Deactivate')
                              : (language === 'ar' ? 'تفعيل' : 'Activate')}
                            data-testid={`toggle-status-${user.id}`}
                          >
                            {user.is_active !== false ? <UserX size={16} /> : <UserCheck size={16} />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(user)}
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit2 size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(user)}
                            disabled={user.id === currentUser.id}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingUser 
                ? (language === 'ar' ? 'تعديل المستخدم' : 'Edit User')
                : (language === 'ar' ? 'إضافة مستخدم جديد' : 'Add New User')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('name')}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="user-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('email')}</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                dir="ltr"
                data-testid="user-email-input"
              />
            </div>

            {!editingUser && (
              <div className="space-y-2">
                <Label>{t('password')}</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="user-password-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>{language === 'ar' ? 'الصلاحية' : 'Role'}</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {language === 'ar' ? role.label_ar : role.label_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                {t('cancel')}
              </Button>
              <Button type="submit" data-testid="user-submit-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {language === 'ar' ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            {selectedUser && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">{language === 'ar' ? 'المستخدم' : 'User'}</p>
                <p className="font-medium">{selectedUser.name}</p>
                <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>{language === 'ar' ? 'كلمة المرور الجديدة' : 'New Password'}</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                data-testid="new-password-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" data-testid="reset-password-submit-btn">
                {language === 'ar' ? 'تغيير كلمة المرور' : 'Reset Password'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
